import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import type { ImperativePanelHandle } from 'react-resizable-panels';
import {
  DEFAULT_MODEL_ID,
  NEW_THREAD_ID,
  TAIL_PRESERVE_MESSAGE_COUNT,
  type ChatMessage,
  type ChatThread,
  type ComposerStyleId,
  type PermissionMode,
} from './chat-types';
import { SEED_THREADS } from './thread-seed';

export const AI_RAIL_OPEN_KEY = 'mock-ui-ai-rail-open';

let nextMessageId = 1000;

function nowHHMM(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function readRailOpen(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(AI_RAIL_OPEN_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeRailOpen(open: boolean) {
  try {
    window.localStorage.setItem(AI_RAIL_OPEN_KEY, open ? 'true' : 'false');
  } catch {
    // Storage may be unavailable.
  }
}

function cloneThreads(): ChatThread[] {
  return SEED_THREADS.map((t) => ({
    ...t,
    messages: t.messages.map((m) => ({ ...m, citations: m.citations?.map((c) => ({ ...c })) })),
  }));
}

type ChatContextValue = {
  threads: ChatThread[];
  currentThreadId: string;
  messages: ChatMessage[];
  /** Messages hidden by manual compact — older turns replaced by a notice row. */
  compactedEarlierCount: number;
  thinking: boolean;
  draft: string;
  setDraft: (text: string) => void;
  editingMessageId: number | null;
  setEditingMessageId: (id: number | null) => void;
  editDraft: string;
  setEditDraft: (text: string) => void;
  modelId: string;
  setModelId: (id: string) => void;
  permissionMode: PermissionMode;
  setPermissionMode: (mode: PermissionMode) => void;
  composerStyle: ComposerStyleId;
  setComposerStyle: (style: ComposerStyleId) => void;
  railOpen: boolean;
  openRail: () => void;
  closeRail: () => void;
  railPanelRef: RefObject<ImperativePanelHandle | null>;
  send: (text: string) => void;
  resend: (text: string) => void;
  compactConversation: () => void;
  newChat: (projectSlug?: string) => void;
  selectThread: (id: string) => void;
  renameThread: (title: string) => void;
  deleteCurrentThread: () => void;
  deleteThread: (id: string) => void;
  toggleThreadPin: (id: string) => void;
  moveThreadToProject: (id: string, slug: string) => void;
  moveThreadOut: (id: string) => void;
  currentTitle: string;
  /** When the active thread is project-scoped, switch to the latest workspace thread or new chat. */
  ensureWorkspaceThread: () => void;
};

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  const [threads, setThreads] = useState<ChatThread[]>(cloneThreads);
  const [currentThreadId, setCurrentThreadId] = useState<string>(NEW_THREAD_ID);
  const [welcomeMessages, setWelcomeMessages] = useState<ChatMessage[]>([]);
  const [welcomeTitle, setWelcomeTitle] = useState('New chat');
  /** Project scope for an ephemeral new chat (`NEW_THREAD_ID`); cleared on workspace new chat. */
  const [newChatProjectSlug, setNewChatProjectSlug] = useState<string | null>(null);
  const [thinking, setThinking] = useState(false);
  const [draft, setDraft] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [modelId, setModelId] = useState(DEFAULT_MODEL_ID);
  const [permissionMode, setPermissionMode] = useState<PermissionMode>('default');
  const [composerStyle, setComposerStyle] = useState<ComposerStyleId>('default');
  const [compactedByThread, setCompactedByThread] = useState<Record<string, number>>({});
  const [railOpen, setRailOpenState] = useState<boolean>(readRailOpen);
  const railPanelRef = useRef<ImperativePanelHandle | null>(null);
  const thinkingTimerRef = useRef<number | null>(null);

  const setRailOpen = useCallback((open: boolean) => {
    setRailOpenState(open);
    writeRailOpen(open);
    const handle = railPanelRef.current;
    if (!handle) return;
    if (open) {
      if (handle.isCollapsed()) handle.expand(25);
    } else if (!handle.isCollapsed()) {
      handle.collapse();
    }
  }, []);

  const openRail = useCallback(() => setRailOpen(true), [setRailOpen]);
  const closeRail = useCallback(() => setRailOpen(false), [setRailOpen]);

  const currentThread = useMemo(
    () => threads.find((t) => t.id === currentThreadId),
    [threads, currentThreadId],
  );

  const messages = currentThreadId === NEW_THREAD_ID ? welcomeMessages : (currentThread?.messages ?? []);

  const compactedEarlierCount = Math.min(
    compactedByThread[currentThreadId] ?? 0,
    Math.max(0, messages.length - TAIL_PRESERVE_MESSAGE_COUNT),
  );

  const currentTitle =
    currentThreadId === NEW_THREAD_ID ? welcomeTitle : (currentThread?.title ?? 'New chat');

  const appendBotReply = useCallback((threadId: string, isWelcome: boolean) => {
    if (thinkingTimerRef.current) window.clearTimeout(thinkingTimerRef.current);
    setThinking(true);
    thinkingTimerRef.current = window.setTimeout(() => {
      const reply: ChatMessage = {
        id: nextMessageId++,
        who: 'bot',
        time: nowHHMM(),
        body:
          'Acknowledged. This assistant is a scripted mock — wire it to your AI backend to answer for real. Two demo sources are loaded in context.',
        citations: [{ label: 'DOC-001' }],
      };
      setThinking(false);
      if (isWelcome) {
        setWelcomeMessages((prev) => [...prev, reply]);
      } else {
        setThreads((prev) =>
          prev.map((t) =>
            t.id === threadId
              ? { ...t, messages: [...t.messages, reply], updatedAt: Date.now() }
              : t,
          ),
        );
      }
    }, 800);
  }, []);

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const userMsg: ChatMessage = {
        id: nextMessageId++,
        who: 'user',
        time: nowHHMM(),
        body: trimmed,
      };
      setDraft('');
      setEditingMessageId(null);
      if (currentThreadId === NEW_THREAD_ID) {
        setWelcomeMessages((prev) => {
          if (prev.length === 0 && welcomeTitle === 'New chat') {
            setWelcomeTitle(trimmed.slice(0, 48));
          }
          return [...prev, userMsg];
        });
        appendBotReply(NEW_THREAD_ID, true);
      } else {
        setThreads((prev) =>
          prev.map((t) =>
            t.id === currentThreadId
              ? {
                  ...t,
                  messages: [...t.messages, userMsg],
                  updatedAt: Date.now(),
                  title: t.title === 'New chat' ? trimmed.slice(0, 48) : t.title,
                }
              : t,
          ),
        );
        appendBotReply(currentThreadId, false);
      }
    },
    [appendBotReply, currentThreadId, welcomeTitle],
  );

  const resend = useCallback(
    (text: string) => {
      send(text);
    },
    [send],
  );

  const compactConversation = useCallback(() => {
    const total = messages.length;
    if (total <= TAIL_PRESERVE_MESSAGE_COUNT) return;
    const hidden = total - TAIL_PRESERVE_MESSAGE_COUNT;
    setCompactedByThread((prev) => ({
      ...prev,
      [currentThreadId]: hidden,
    }));
  }, [currentThreadId, messages.length]);

  const newChat = useCallback((projectSlug?: string) => {
    setCurrentThreadId(NEW_THREAD_ID);
    setWelcomeMessages([]);
    setWelcomeTitle('New chat');
    setNewChatProjectSlug(projectSlug ?? null);
    setDraft('');
    setEditingMessageId(null);
    setThinking(false);
    if (thinkingTimerRef.current) window.clearTimeout(thinkingTimerRef.current);
    setCompactedByThread((prev) => {
      if (!(NEW_THREAD_ID in prev)) return prev;
      const next = { ...prev };
      delete next[NEW_THREAD_ID];
      return next;
    });
  }, []);

  const selectThread = useCallback((id: string) => {
    setCurrentThreadId(id);
    setNewChatProjectSlug(null);
    setDraft('');
    setEditingMessageId(null);
    setThinking(false);
    if (thinkingTimerRef.current) window.clearTimeout(thinkingTimerRef.current);
  }, []);

  const ensureWorkspaceThread = useCallback(() => {
    const isProjectScopedNewChat =
      currentThreadId === NEW_THREAD_ID && newChatProjectSlug !== null;
    const active = threads.find((t) => t.id === currentThreadId);
    const isProjectScopedThread = Boolean(active?.projectSlug);
    if (!isProjectScopedNewChat && !isProjectScopedThread) return;

    const workspaceThreads = threads.filter((t) => !t.projectSlug);
    if (workspaceThreads.length === 0) {
      newChat();
      return;
    }
    const latest = workspaceThreads.reduce((a, b) => (b.updatedAt > a.updatedAt ? b : a));
    selectThread(latest.id);
  }, [currentThreadId, newChatProjectSlug, threads, newChat, selectThread]);

  const renameThread = useCallback(
    (title: string) => {
      const trimmed = title.trim();
      if (!trimmed) return;
      if (currentThreadId === NEW_THREAD_ID) {
        setWelcomeTitle(trimmed);
        return;
      }
      setThreads((prev) =>
        prev.map((t) => (t.id === currentThreadId ? { ...t, title: trimmed } : t)),
      );
    },
    [currentThreadId],
  );

  const deleteCurrentThread = useCallback(() => {
    newChat();
  }, [newChat]);

  const deleteThread = useCallback(
    (id: string) => {
      const deleted = threads.find((t) => t.id === id);
      setThreads((prev) => prev.filter((t) => t.id !== id));
      if (currentThreadId === id) {
        newChat(deleted?.projectSlug);
      }
    },
    [currentThreadId, threads, newChat],
  );

  const toggleThreadPin = useCallback((id: string) => {
    setThreads((prev) =>
      prev.map((t) => (t.id === id ? { ...t, pinned: !t.pinned } : t)),
    );
  }, []);

  const moveThreadToProject = useCallback((id: string, slug: string) => {
    setThreads((prev) =>
      prev.map((t) => (t.id === id ? { ...t, projectSlug: slug } : t)),
    );
  }, []);

  const moveThreadOut = useCallback((id: string) => {
    setThreads((prev) =>
      prev.map((t) => (t.id === id ? { ...t, projectSlug: undefined } : t)),
    );
  }, []);

  const value = useMemo<ChatContextValue>(
    () => ({
      threads,
      currentThreadId,
      messages,
      compactedEarlierCount,
      thinking,
      draft,
      setDraft,
      editingMessageId,
      setEditingMessageId,
      editDraft,
      setEditDraft,
      modelId,
      setModelId,
      permissionMode,
      setPermissionMode,
      composerStyle,
      setComposerStyle,
      railOpen,
      openRail,
      closeRail,
      railPanelRef,
      send,
      resend,
      compactConversation,
      newChat,
      selectThread,
      renameThread,
      deleteCurrentThread,
      deleteThread,
      toggleThreadPin,
      moveThreadToProject,
      moveThreadOut,
      currentTitle,
      ensureWorkspaceThread,
    }),
    [
      threads,
      currentThreadId,
      messages,
      compactedEarlierCount,
      thinking,
      draft,
      editingMessageId,
      editDraft,
      modelId,
      permissionMode,
      composerStyle,
      railOpen,
      openRail,
      closeRail,
      send,
      resend,
      compactConversation,
      newChat,
      selectThread,
      renameThread,
      deleteCurrentThread,
      deleteThread,
      toggleThreadPin,
      moveThreadToProject,
      moveThreadOut,
      currentTitle,
      ensureWorkspaceThread,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used inside <ChatProvider>');
  return ctx;
}
